import type { FC, PropsWithChildren } from 'react'
import styled from '@emotion/styled'

import Typography from '@segment/matcha/components/Typography'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

import DeliveryImage from './DeliveryImage'

const data = {
  heading: `Delivery`,
  section1: `Once you finally have data in one spot, there’s one last step—using that data. That means getting it into all of the different consumers and end tools.`,
  section2: `In a controlled environment, this is easy. But over the open internet, it’s anything but.`,
  section3: `You’re connecting to dozens of APIs. What if one fails? What if it gets slow? What if an API mysteriously changes? How do you ensure nothing goes missing?`,
  section4: `You have all sorts of semi-structured data. How do you map it into structured forms like a data lake or a data warehouse.`,
  section5: `It’s not easy, but here’s how we’ve built it.`,
  link1: `https://segment.com/blog/introducing-centrifuge/`,
  link1Text: `Centrifuge: a reliable system for delivering billions of events per day (65 comments)`,
  link2: `https://news.ycombinator.com/item?id=17499137`,
  link2Text: `Goodbye microservices (753 comments)`,
  link3: `https://github.com/segmentio/kafka-go`,
  link3Text: `segmentio/kafka-go (2,500 stars)`,
}

const Delivery: FC<PropsWithChildren<unknown>> = () => {
  const pageConfig = usePageConfig()
  const {
    heading,
    section1,
    section2,
    section3,
    section4,
    section5,
    link1,
    link1Text,
    link2,
    link2Text,
    link3,
    link3Text,
  } = data
  return (
    <Wrapper>
      <SectionBase variant="2Col" copyVariant="wide">
        <Section.Copy>
          <CopyTextBase>
            <CopyText.Heading>{heading}</CopyText.Heading>
            <CopyText.Description tag="div">
              <CopyTextDescriptionSection>{section1}</CopyTextDescriptionSection>
              <CopyTextDescriptionSection>{section2}</CopyTextDescriptionSection>
              <CopyTextDescriptionSection>{section3}</CopyTextDescriptionSection>
              <CopyTextDescriptionSection>{section4}</CopyTextDescriptionSection>
              <CopyTextDescriptionSection>{section5}</CopyTextDescriptionSection>
              <CopyTextDescriptionSection>
                <Typography variant="pLarge">
                  <a href={getAbsoluteUrl(link1, pageConfig)}>{link1Text}</a>
                </Typography>
              </CopyTextDescriptionSection>
              <CopyTextDescriptionSection>
                <Typography variant="pLarge">
                  <a href={link2}>{link2Text}</a>
                </Typography>
              </CopyTextDescriptionSection>
              <CopyTextDescriptionSection>
                <Typography variant="pLarge">
                  <a href={link3}>{link3Text}</a>
                </Typography>
              </CopyTextDescriptionSection>
            </CopyText.Description>
          </CopyTextBase>
        </Section.Copy>
        <Section.Contents>
          <DeliveryImage />
        </Section.Contents>
      </SectionBase>
    </Wrapper>
  )
}

const CopyTextDescriptionSection = styled.div`
  margin-bottom: 32px;
`

const Wrapper = styled.section`
  display: grid;
`

export default Delivery
